import { AppraisalQuestion } from '../useAppraisalFlow'
import useTranslation from 'next-translate/useTranslation'
import { Button } from '@/components/Button'
import { EAppraisalQuestionInputType } from '@/types/gql/graphql'
import { cn } from '@/utils/cn'
import NumberInput from './NumberInput'
import OptionGrid from './OptionGrid'
import TextInput from './TextInput'

export interface QuestionListProps {
  questions: AppraisalQuestion[]
  answers: Record<string, string>
  canSubmit: boolean
  /** False when more questions follow — the button reads "Weiter" then. */
  isFinalStep: boolean
  onAnswerChange: (key: string, value: string | null) => void
  onSubmit: () => void
}

export default function QuestionList({
  questions,
  answers,
  canSubmit,
  isFinalStep,
  onAnswerChange,
  onSubmit,
}: QuestionListProps) {
  const { t } = useTranslation('wertCheck')

  return (
    <div className="flex flex-col gap-6">
      {questions.map((question) => (
        <section
          key={question.key}
          className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-4 flex flex-col gap-3"
        >
          {question.skippable ? (
            // known / unknown toggle beside the label; unknown submits ''
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-heading-2xs text-primary uppercase tracking-wide">
                {question.title}
              </h2>
              <div
                className="flex rounded-full border border-neutral-300 p-0.5"
                role="radiogroup"
                aria-label={question.title}
              >
                {(['known', 'unknown'] as const).map((choice) => {
                  const selected =
                    (answers[question.key] === '') === (choice === 'unknown')
                  return (
                    <button
                      key={choice}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={cn(
                        'rounded-full px-3 py-1 text-s transition-colors',
                        selected
                          ? 'bg-brand-700 text-primary-white'
                          : 'text-secondary hover:text-primary',
                      )}
                      onClick={() =>
                        onAnswerChange(
                          question.key,
                          choice === 'unknown' ? '' : null,
                        )
                      }
                    >
                      {t(choice)}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : (
            <h2 className="text-heading-2xs text-primary text-center uppercase tracking-wide">
              {question.title}
            </h2>
          )}

          {question.hint && (
            <p className="text-s text-secondary text-center">{question.hint}</p>
          )}

          {question.skippable &&
          answers[question.key] === '' ? null : question.inputType ===
            EAppraisalQuestionInputType.Number ? (
            <NumberInput
              value={answers[question.key] ?? ''}
              unit={question.unit}
              placeholder={question.placeholder}
              range={question}
              onChange={(value) =>
                onAnswerChange(question.key, value === '' ? null : value)
              }
            />
          ) : question.inputType === EAppraisalQuestionInputType.Text ? (
            <TextInput
              value={answers[question.key] ?? ''}
              placeholder={question.placeholder}
              onChange={(value) =>
                onAnswerChange(question.key, value.trim() === '' ? null : value)
              }
            />
          ) : (
            <OptionGrid
              question={question}
              value={answers[question.key]}
              onChange={(value) => onAnswerChange(question.key, value)}
            />
          )}
        </section>
      ))}

      <Button
        type="button"
        variant="cta"
        className="w-full"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        {t(isFinalStep ? 'submit_answers' : 'next_step')}
      </Button>
    </div>
  )
}
